import React, { useEffect, useRef, useState } from 'react';
import type { ClaimDraftingSession, SessionItemDraft } from '../../services/solutionClaimsService';
import { solutionClaimsService } from '../../services/solutionClaimsService';
import type { ClaimableItem } from '../../services/claimableItemsService';
import { CAT_LABELS, CAT_COLORS, CAT_BG, btnPrimary, btnSecondary, btnDanger } from './claimsConstants';

export interface DraftingStepProps {
  subProjectId: string;
  solutionId: string;
  items: ClaimableItem[];
  onDone: () => void;
  t: (k: string, fb?: string) => string;
}

type Substep = 'loading_aspects' | 'review_aspects' | 'finalizing' | 'done';

const SESSION_STORAGE_KEY = (spId: string, solId: string) =>
  `claims_session_${spId}_${solId}`;

const Spinner: React.FC = () => (
  <div style={{ width: 14, height: 14, border: '2px solid #2563eb', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.7s linear infinite', flexShrink: 0 }} />
);

const DraftingStep: React.FC<DraftingStepProps> = ({ subProjectId, solutionId, items: _items, onDone, t }) => {
  const [substep, setSubstep] = useState<Substep>('loading_aspects');
  const [session, setSession] = useState<ClaimDraftingSession | null>(null);
  // editedAspects: item_id -> aspects array (user edits)
  const [editedAspects, setEditedAspects] = useState<Record<string, string[]>>({});
  const [error, setError] = useState<string | null>(null);
  const [claimsCount, setClaimsCount] = useState(0);
  const [sessionRestored, setSessionRestored] = useState(false);
  const didInit = useRef(false);

  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;
    loadAspects();
  }, []);

  const loadAspects = async (force = false) => {
    setSubstep('loading_aspects');
    setError(null);

    // Try to restore from localStorage first (unless forcing)
    if (!force) {
      const savedId = localStorage.getItem(SESSION_STORAGE_KEY(subProjectId, solutionId));
      if (savedId) {
        try {
          const restored = await solutionClaimsService.getSession(subProjectId, solutionId, savedId);
          applySession(restored, true);
          return;
        } catch {
          localStorage.removeItem(SESSION_STORAGE_KEY(subProjectId, solutionId));
        }
      }
    }

    try {
      const sess = await solutionClaimsService.generateAspects(subProjectId, solutionId, force);
      applySession(sess, false);
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(
        typeof detail === 'string'
          ? detail
          : t('claimsWorkspace.step3.errorAspects', 'Failed to draft aspects'),
      );
      setSubstep('review_aspects');
    }
  };

  const applySession = (sess: ClaimDraftingSession, restored: boolean) => {
    setSession(sess);
    const initial: Record<string, string[]> = {};
    for (const it of sess.items) {
      initial[it.item_id] = [...it.aspects];
    }
    setEditedAspects(initial);
    localStorage.setItem(SESSION_STORAGE_KEY(subProjectId, solutionId), sess.id);
    setSessionRestored(restored);
    setSubstep('review_aspects');
  };

  const handleFinalize = async () => {
    if (!session) return;
    setSubstep('finalizing');
    setError(null);
    try {
      const itemsPayload = session.items.map((it: SessionItemDraft) => ({
        item_id: it.item_id,
        aspects: editedAspects[it.item_id] ?? it.aspects,
      }));
      const claims = await solutionClaimsService.finalizeClaims(
        subProjectId, solutionId, session.id, itemsPayload,
      );
      setClaimsCount(claims.length);
      localStorage.removeItem(SESSION_STORAGE_KEY(subProjectId, solutionId));
      setSubstep('done');
      setTimeout(onDone, 1800);
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(
        typeof detail === 'string'
          ? detail
          : t('claimsWorkspace.step3.errorFinalize', 'Failed to generate claims'),
      );
      setSubstep('review_aspects');
    }
  };

  const addAspect = (itemId: string) => {
    setEditedAspects(prev => ({ ...prev, [itemId]: [...(prev[itemId] ?? []), ''] }));
  };

  const updateAspect = (itemId: string, idx: number, val: string) => {
    setEditedAspects(prev => {
      const arr = [...(prev[itemId] ?? [])];
      arr[idx] = val;
      return { ...prev, [itemId]: arr };
    });
  };

  const removeAspect = (itemId: string, idx: number) => {
    setEditedAspects(prev => {
      const arr = [...(prev[itemId] ?? [])];
      arr.splice(idx, 1);
      return { ...prev, [itemId]: arr };
    });
  };

  return (
    <div style={{ padding: '16px', overflowY: 'auto', height: '100%', maxWidth: 600 }}>
      <span style={{ fontSize: 12, fontWeight: 700, color: '#1e293b', display: 'block', marginBottom: 12 }}>
        {t('claimsWorkspace.step3.title', 'Draft Claims')}
      </span>

      {substep === 'loading_aspects' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, color: '#475569' }}>
          <Spinner />
          {t('claimsWorkspace.step3.loadingAspects', 'Drafting independent claims and identifying aspects…')}
        </div>
      )}

      {substep === 'done' && (
        <div style={{ padding: '10px 14px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 4, fontSize: 12, color: '#15803d', fontWeight: 600 }}>
          {t('claimsWorkspace.step3.done', `${claimsCount} claims generated. Advancing to review…`).replace('${count}', String(claimsCount))}
        </div>
      )}

      {substep === 'finalizing' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, color: '#475569' }}>
          <Spinner />
          {t('claimsWorkspace.step3.generating', 'Generating dependent claims and homogenizing wording…')}
        </div>
      )}

      {substep === 'review_aspects' && (
        <>
          {sessionRestored && (
            <div style={{ fontSize: 11, color: '#2563eb', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 3, padding: '4px 8px', marginBottom: 10 }}>
              {t('claimsWorkspace.step3.sessionRestored', 'Resumed from previous session. Review aspects and generate claims.')}
            </div>
          )}
          {error && (
            <div style={{ fontSize: 11, color: '#dc2626', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 3, padding: '4px 8px', marginBottom: 10 }}>{error}</div>
          )}
          {!session || session.items.length === 0 ? (
            <div style={{ fontSize: 11, color: '#94a3b8' }}>
              {t('claimsWorkspace.step3.noItems', 'No ranked claimable items. Go back to Step 3.')}
            </div>
          ) : (
            <>
              {session.items.map((it: SessionItemDraft) => {
                const aspects = editedAspects[it.item_id] ?? it.aspects;
                const catBg = CAT_BG[it.item_category as keyof typeof CAT_BG] ?? '#e0e7ff';
                const catColor = CAT_COLORS[it.item_category as keyof typeof CAT_COLORS] ?? '#3730a3';
                return (
                  <div key={it.item_id} style={{ border: '1px solid #e2e8f0', borderRadius: 4, marginBottom: 12, padding: '10px 12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                      <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 3, background: catBg, color: catColor, fontWeight: 700, textTransform: 'uppercase' }}>
                        {CAT_LABELS[it.item_category as keyof typeof CAT_LABELS] ?? it.item_category}
                      </span>
                      <span style={{ fontSize: 12, fontWeight: 600, color: '#1e293b' }}>{it.item_label}</span>
                    </div>

                    <div style={{ fontSize: 11, color: '#475569', fontStyle: 'italic', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 3, padding: '6px 8px', marginBottom: 8 }}>
                      <span style={{ fontWeight: 600, fontStyle: 'normal', display: 'block', marginBottom: 2, color: '#64748b', fontSize: 10, textTransform: 'uppercase' }}>
                        {t('claimsWorkspace.step3.independentClaim', 'Independent claim')}
                      </span>
                      {it.independent_claim_text}
                    </div>

                    <div style={{ fontSize: 10, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', marginBottom: 4 }}>
                      {t('claimsWorkspace.step3.aspects', 'Aspects')}
                    </div>
                    {aspects.map((asp, idx) => (
                      <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: 4, marginBottom: 4 }}>
                        <input
                          type="text"
                          value={asp}
                          placeholder={t('claimsWorkspace.step3.aspectPlaceholder', 'Describe a claimable aspect…')}
                          onChange={e => updateAspect(it.item_id, idx, e.target.value)}
                          style={{ flex: 1, fontSize: 11, padding: '3px 6px', border: '1px solid #cbd5e1', borderRadius: 3 }}
                        />
                        <button onClick={() => removeAspect(it.item_id, idx)} style={{ ...btnDanger, padding: '2px 6px', fontSize: 10 }}>×</button>
                      </div>
                    ))}
                    <button onClick={() => addAspect(it.item_id)} style={{ ...btnSecondary, fontSize: 10, padding: '2px 8px', marginTop: 2 }}>
                      {t('claimsWorkspace.step3.addAspect', '+ Add aspect')}
                    </button>
                  </div>
                );
              })}

              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                <button onClick={handleFinalize} style={btnPrimary}>
                  {t('claimsWorkspace.step3.generateButton', 'Generate claims')}
                </button>
                <button onClick={() => loadAspects(true)} style={{ ...btnSecondary, fontSize: 11 }}>
                  {t('common.regenerate', 'Regenerate')}
                </button>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default DraftingStep;
